import { O, pipe, RA } from '@packages/prelude'
import type * as domain from '@packages/domain'
import { nonBlank } from '~/commons/string'

// Key names come from the domain type (single source of truth, via the interface — no `.props`).
// The render ORDER is a presentation concern and lives here in the view/state.
export type Slot = keyof domain.StepHeadings.Type

export const slotOrder: ReadonlyArray<Slot> = ['image', 'message', 'preview']

export interface NumberedStep {
  n: number
  text: string
}

// Product override wins over site default; blank/whitespace counts as absent.
const pickHeading =
  (product: O.Option<domain.StepHeadings.Type>, site: O.Option<domain.StepHeadings.Type>) =>
  (slot: Slot): O.Option<string> =>
    pipe(
      product,
      O.chain(h => nonBlank(h[slot] ?? '')),
      O.alt(() =>
        pipe(
          site,
          O.chain(h => nonBlank(h[slot] ?? ''))
        )
      )
    )

export const resolveHeadings = (
  product: O.Option<domain.StepHeadings.Type>,
  site: O.Option<domain.StepHeadings.Type>
): Record<Slot, O.Option<string>> => {
  const pick = pickHeading(product, site)

  // Literal keys give compile-time exhaustiveness against Slot (adding a domain slot fails here).
  return { image: pick('image'), message: pick('message'), preview: pick('preview') }
}

// Numbers only the blocks that are present AND carry a heading, 1..n in slot order, no gaps.
export const numberedSteps = (
  resolved: Record<Slot, O.Option<string>>,
  presence: Record<Slot, boolean>
): Record<Slot, O.Option<NumberedStep>> => {
  const acc = RA.reduce(
    { n: 0, acc: { image: O.none, message: O.none, preview: O.none } as Record<Slot, O.Option<NumberedStep>> },
    (state: { n: number; acc: Record<Slot, O.Option<NumberedStep>> }, slot: Slot) => {
      const headingOpt = resolved[slot] as O.Option<string> | undefined
      const isPresent = presence[slot] as boolean | undefined

      return pipe(
        headingOpt as O.Option<string>,
        O.filter(() => isPresent === true),
        O.match(
          () => state,
          text => {
            const n = state.n + 1
            return { n, acc: { ...state.acc, [slot]: O.some({ n, text }) } }
          }
        )
      )
    }
  )(slotOrder)

  return acc.acc
}
